// Live cursors for panzoom canvas surfaces (tickets board/epics, /studios,
// /team-and-agents, /files, /infra, analytics map — everything that boots
// through initPanzoomBoard). The PROJECT canvas keeps its original
// CollabService flow (sdk.sendCursor → root.clients → ViewSvitcher
// OverlayPeers); this module gives every OTHER canvas page the same
// functionality over the /user-socket presence relay (server
// packages/socket/user.js `presence:cursor` — volatile, room-gated).
//
// Channel model: one presence subscription per SURFACE scope
// (`cursor:<workspaceId>:<path>`), ref-counted across subscribers the same
// way presence.js ref-counts the shell channel. Peers are keyed by peerId
// (socket id) — two tabs of one user are two cursors, matching the collab
// canvas. High-frequency by design: peer positions NEVER touch root state —
// subscribers get direct (peerId, peer|null) callbacks and paint nodes
// imperatively (the same altitude as the panzoom/dots layer itself).

import { getSDK } from './sdk.js'

// FA204 / frank trap — `_channels` used to live at module scope
// (genuine cross-call state — a Map of live channels shared across every
// independent liveCursorsJoin caller) and `_wsIdFor`/`_surfaceScopeFor`/
// `_notify`/the throttle consts were bare module-local helpers referenced
// from the two exports below. `_channels` is re-homed onto
// `window.__liveCursorsChannels` per the pointerCapability.js template;
// `_wsIdFor`/`_surfaceScopeFor` are duplicated into both exports that share
// them, and `_notify`/the consts (used only by liveCursorsJoin) are
// inlined there.

// The scope the CURRENT surface should be on — lets the overlay's lifecheck
// detect that its channel was joined against a stale mid-boot path (the URL
// canonicalizes async during boot: '/' → '/w/<ws>' → the real page path) and
// re-join. Registered: `el.call('liveCursorsScope')`. Null while auth/root
// aren't ready.
export function liveCursorsScope() {
  const wsIdFor = (root) => root?.jwtClaims?.active_workspace_id || root?.workspace?.id || 'default'
  const surfaceScopeFor = (root) => `cursor:${wsIdFor(root)}:${root?.currentPath || '/'}`

  const root = this?.state?.root
  if (!root || !root.currentUserEmail) return null
  return surfaceScopeFor(root)
}

// Join the live-cursor channel for the CURRENT surface (workspace + path).
// Registered function — call as `el.call('liveCursorsJoin', onPeer)`.
// onPeer(peerId, peer|null): peer = { x, y, userKey, name, userId, at } in
// CONTENT coordinates; null = peer left / went stale. Returns
// { scope, send(data), leave() } — send is throttled internally.
export function liveCursorsJoin(onPeer) {
  const wsIdFor = (root) => root?.jwtClaims?.active_workspace_id || root?.workspace?.id || 'default'
  const surfaceScopeFor = (root) => `cursor:${wsIdFor(root)}:${root?.currentPath || '/'}`
  const notify = (ch, peerId, peer) => {
    for (const fn of ch.subs) {
      try {
        fn(peerId, peer)
      } catch (_) {
        /* one bad painter must not break the rest */
      }
    }
  }
  const STALE_PEER_MS = 6000
  const SWEEP_MS = 3000
  const SEND_THROTTLE_MS = 45

  const el = this
  const root = el?.state?.root
  const me = root?.currentUserEmail
  if (!root || !me || typeof onPeer !== 'function') {
    return { scope: null, send: () => {}, leave: () => {} }
  }
  const scope = surfaceScopeFor(root)

  // scope -> { refs, unsub, peers: Map<peerId, peer>, subs: Set<fn>, sweep }
  const w = typeof window !== 'undefined' ? window : globalThis
  if (!w.__liveCursorsChannels) w.__liveCursorsChannels = new Map()
  const channels = w.__liveCursorsChannels

  let ch = channels.get(scope)
  if (!ch) {
    ch = { refs: 0, unsub: null, closed: false, peers: new Map(), subs: new Set(), sweep: null }
    channels.set(scope, ch)

    const myName = (() => {
      const person = (root.people || []).find(
        (p) => p?.email && String(p.email).toLowerCase() === String(me).toLowerCase()
      )
      return person?.name || String(me).split('@')[0]
    })()

    try {
      const sdk = getSDK()
      // The shell's SDK bootstrap wraps every method in an init gate that
      // returns a PROMISE of the real return value — storing it directly left
      // ch.unsub as a Promise, so `.sendCursor` was undefined and every send
      // silently no-op'd (cursors never left the tab). Resolve it, then bind.
      const pending = sdk?.subscribePresence?.(
        {
          scope,
          userKey: me,
          meta: { app: 'cursor' },
          onCursor: (payload) => {
            const peerId = payload?.peerId
            if (!peerId) return
            if (payload.gone) {
              if (ch.peers.delete(peerId)) notify(ch, peerId, null)
              return
            }
            const peer = {
              x: +payload.x || 0,
              y: +payload.y || 0,
              userKey: payload.userKey || payload.userId || 'peer',
              name: payload.name || null,
              userId: payload.userId || null,
              at: Date.now()
            }
            ch.peers.set(peerId, peer)
            notify(ch, peerId, peer)
          }
        },
        () => {
          /* roster sync unused here — cursor lifecycle is event + sweep driven */
        }
      )
      Promise.resolve(pending)
        .then((u) => {
          if (ch.closed) {
            try {
              if (typeof u === 'function') u()
            } catch (_) {}
            return
          }
          ch.unsub = typeof u === 'function' ? u : null
        })
        .catch((err) => console.warn('[liveCursors] subscribe failed:', err?.message || err))
      ch.myName = myName
    } catch (err) {
      console.warn('[liveCursors] subscribe failed:', err?.message || err)
    }

    // Stale sweep: a peer that stopped moving (tab hidden, network drop the
    // server's gone-event missed) fades out instead of freezing forever.
    ch.sweep = setInterval(() => {
      const now = Date.now()
      for (const [peerId, peer] of ch.peers) {
        if (now - peer.at > STALE_PEER_MS) {
          ch.peers.delete(peerId)
          notify(ch, peerId, null)
        }
      }
    }, SWEEP_MS)
  }

  ch.refs++
  ch.subs.add(onPeer)
  // Late joiner sees peers already in flight.
  for (const [peerId, peer] of ch.peers) {
    try {
      onPeer(peerId, peer)
    } catch (_) {}
  }

  let lastSent = 0
  let trailing = null
  const send = (data = {}) => {
    const fire = () => {
      lastSent = Date.now()
      try {
        ch.unsub?.sendCursor?.({ ...data, name: ch.myName })
      } catch (_) {
        /* socket mid-reconnect — next move resends */
      }
    }
    const wait = SEND_THROTTLE_MS - (Date.now() - lastSent)
    if (wait <= 0) {
      if (trailing) {
        clearTimeout(trailing)
        trailing = null
      }
      fire()
    } else {
      // Trailing send so the FINAL resting position always lands.
      if (trailing) clearTimeout(trailing)
      trailing = setTimeout(fire, wait)
    }
  }

  let left = false
  const leave = () => {
    if (left) return
    left = true
    if (trailing) clearTimeout(trailing)
    ch.subs.delete(onPeer)
    ch.refs = Math.max(0, ch.refs - 1)
    if (ch.refs === 0) {
      clearInterval(ch.sweep)
      // `closed` covers the still-resolving init-gate promise: its .then sees
      // the flag and disconnects the socket the moment it lands.
      ch.closed = true
      try {
        ch.unsub?.sendCursor?.({ gone: true })
      } catch (_) {}
      try {
        ch.unsub?.()
      } catch (_) {}
      channels.delete(scope)
    }
  }

  return { scope, send, leave }
}
